import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, SectionHead } from "../components/Reveal";
import { CASE_FILTERS } from "../data/content";
import { api } from "../api";

export default function Work() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/case-studies")
      .then((r) => setItems(r.data.items))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter === "All" ? items : items.filter((cs) => (cs.categories || []).includes(filter));

  return (
    <main className="pt-32 pb-24" data-testid="work-page">
      <Seo title="Our Work" description="Case studies from Trendsight Media: social media, branding, influencer, advertising and performance campaigns that moved brands forward." />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="font-mono-accent text-xs uppercase tracking-[0.35em] text-blue-400 mb-5">
          Case Studies
        </motion.p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-50 leading-[1.02]" data-testid="work-page-headline">
          IDEAS THAT MOVE <span className="text-gradient">BRANDS FORWARD.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed">
          Challenge, strategy, execution, result — every engagement, broken down. Current entries are illustrative demonstrations of our format and capabilities.
        </p>

        <div className="mt-10 flex flex-wrap gap-2.5" data-testid="work-filters">
          {CASE_FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`work-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-full px-4 py-2 text-[13px] font-semibold transition-all duration-300 active:scale-95 ${
                filter === f ? "bg-blue-600 text-white shadow-lg shadow-blue-600/25" : "border border-slate-700 text-slate-400 hover:border-blue-500/50 hover:text-blue-300"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mt-20 flex justify-center" data-testid="work-loading">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((cs, i) => (
              <Reveal key={cs.id} delay={(i % 3) * 0.08}>
                <Link
                  to={`/work/${cs.slug}`}
                  data-testid={`case-card-${cs.slug}`}
                  className="group block h-full rounded-2xl border border-slate-800 bg-slate-900/60 overflow-hidden hover:border-blue-500/50 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300"
                >
                  <div className="h-44 bg-gradient-to-br from-blue-600/25 via-slate-900 to-cyan-500/15 relative">
                    <span className="absolute bottom-4 left-5 font-mono-accent text-[10px] uppercase tracking-widest text-blue-300/80">{cs.industry}</span>
                    {cs.is_demo && (
                      <span className="absolute top-4 right-4 text-[9px] font-mono-accent uppercase tracking-widest text-slate-400 border border-slate-700 rounded-full px-2.5 py-1">Illustrative</span>
                    )}
                  </div>
                  <div className="p-6">
                    <h2 className="font-display text-lg font-bold text-slate-100 leading-snug group-hover:text-blue-300 transition-colors">{cs.title}</h2>
                    <p className="text-[13px] text-slate-400 mt-2 leading-relaxed line-clamp-2">{cs.challenge}</p>
                    <div className="flex flex-wrap gap-1.5 mt-4">
                      {(cs.results || []).slice(0, 3).map((r) => (
                        <span key={r.label} className="text-[10px] text-emerald-400/90 border border-emerald-500/25 rounded-full px-2.5 py-1">
                          {r.value} {r.label}
                        </span>
                      ))}
                    </div>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-blue-400 group-hover:text-blue-300 transition-colors">
                      Read case study <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <p className="mt-16 text-center text-slate-500" data-testid="work-empty">No case studies in this category yet.</p>
        )}
      </div>
    </main>
  );
}
